'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';

interface SearchResult {
  title: string;
  slug: string;
  excerpt: string;
  categorySlug: string;
  categoryName: string;
}

export default function HelpSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/help-search?q=${encodeURIComponent(q)}`);
      const data: SearchResult[] = await res.json();
      setResults(data);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value.trim()), 300);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl mx-auto">
      <div className="relative">
        {/* Search icon */}
        <svg
          className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>

        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search help articles…"
          className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/15 transition-all text-sm"
        />

        {loading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <svg className="w-4 h-4 animate-spin text-white/50" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}
      </div>

      {/* Results dropdown */}
      {open && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-stone-200/60 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-stone-400">
              No articles found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <div className="py-1">
              {results.map((r) => (
                <Link
                  key={`${r.categorySlug}/${r.slug}`}
                  href={`/help/${r.categorySlug}/${r.slug}`}
                  className="block px-5 py-3 hover:bg-stone-50 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <span className="text-sm font-semibold text-stone-800 block leading-tight">
                    {r.title}
                  </span>
                  {r.categoryName && (
                    <span className="text-xs text-stone-400 mt-0.5 block">
                      {r.categoryName}
                    </span>
                  )}
                  {r.excerpt && (
                    <span className="text-xs text-stone-400 mt-1 line-clamp-1 block">
                      {r.excerpt}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
